import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { X, Lock, CheckCircle2, ArrowRight, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";

const BENEFITS_ARTWORK = [
  "Storia completa narrata da voce esperta",
  "Testo integrale dell'opera",
  "Ascolto illimitato, per sempre",
  "Pagamento sicuro Stripe",
];
const BENEFITS_MUSEUM = [
  "Tutte le opere del museo sbloccate",
  "Audio illimitato per ogni capolavoro",
  "Risparmi rispetto agli acquisti singoli",
  "Pagamento sicuro Stripe",
];

export default function Paywall() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const kind = params.get("kind");
  const isMuseumPass = kind === "museum_pass";

  const [artwork, setArtwork] = useState(null);
  const [museum, setMuseum] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    if (isMuseumPass) {
      api.getMuseum(id).then((r) => setMuseum(r.museum)).catch(() => {});
    } else {
      api.getArtwork(id).then(setArtwork).catch(() => {});
    }
  }, [id, isMuseumPass]);

  async function startCheckout() {
    if (!id) return;
    setBusy(true);
    try {
      const origin = window.location.origin;
      const res = await api.createCheckout(
        isMuseumPass ? { museum_id: id, origin_url: origin } : { artwork_id: id, origin_url: origin }
      );
      window.sessionStorage.setItem("qrt_session_id", res.session_id);
      window.location.href = res.url;
    } catch (e) {
      toast.error("Impossibile avviare il pagamento", { description: e?.message || "" });
      setBusy(false);
    }
  }

  const title = isMuseumPass ? museum?.name ?? "Pass museo" : artwork?.title ?? "Storia completa";
  const subtitle = isMuseumPass ? "Accesso illimitato a tutte le opere" : artwork?.artist ?? "";
  const amount = isMuseumPass ? (museum?.pass_price_eur ?? 9) : (artwork?.price_eur ?? 4);
  const benefits = isMuseumPass ? BENEFITS_MUSEUM : BENEFITS_ARTWORK;

  return (
    <div className="min-h-screen bg-bg page-enter">
      <div className="max-w-md mx-auto px-5 py-8 pb-12">
        <div className="flex items-center justify-between mb-8">
          <button
            data-testid="paywall-close-btn"
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center hover:bg-black/5 rounded-full transition"
          >
            <X className="text-ink" size={24} />
          </button>
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-border rounded-full">
            <Lock size={11} className="text-ink-soft" />
            <span className="text-[11px] font-medium text-ink-soft">Pagamento sicuro</span>
          </div>
        </div>

        <div className="text-[11px] tracking-[0.3em] font-semibold text-gold mb-2">
          {isMuseumPass ? "PASS MUSEO" : "SBLOCCO OPERA"}
        </div>
        <h1 data-testid="paywall-title" className="font-heading font-bold text-4xl sm:text-5xl leading-[1.02] tracking-tight text-ink">
          {isMuseumPass ? "Sblocca tutto il museo" : "Sblocca la storia completa"}
        </h1>
        <div className="mt-3 font-semibold text-lg text-ink">{title}</div>
        {subtitle && <div className="text-sm text-ink-soft">{subtitle}</div>}

        <div className="bg-brand text-white rounded-3xl p-7 mt-6 text-center shadow-xl shadow-brand/20">
          <div className="text-[11px] tracking-[0.25em] font-semibold text-white/85">
            {isMuseumPass ? "Pass illimitato" : "Sblocco una tantum"}
          </div>
          <div data-testid="paywall-price" className="font-heading font-bold text-6xl mt-1 tracking-tight">
            €{Number(amount).toFixed(2)}
          </div>
          <div className="text-xs text-white/85 mt-1">Una volta sbloccato, è tuo per sempre.</div>
        </div>

        <div className="space-y-2.5 mt-6">
          {benefits.map((b, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border border-border rounded-2xl p-3.5">
              <CheckCircle2 className="text-emerald-500 shrink-0" size={20} />
              <span className="font-medium text-sm text-ink">{b}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 bg-brand/5 px-4 py-3 rounded-xl mt-6">
          <Smartphone size={16} className="text-ink-soft" />
          <span className="text-xs font-medium text-ink">
            Su iPhone e Android: paga in un tap con Apple Pay / Google Pay
          </span>
        </div>

        <button
          data-testid="paywall-pay-btn"
          onClick={startCheckout}
          disabled={busy}
          className="w-full mt-8 inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-white py-5 rounded-full font-bold text-base shadow-lg shadow-gold/30 disabled:opacity-60 transition"
        >
          {busy ? <Loader2 className="animate-spin" /> : (
            <>
              Sblocca ora · €{Number(amount).toFixed(2)} <ArrowRight size={20} />
            </>
          )}
        </button>

        <p className="text-xs text-ink-soft text-center mt-6 leading-relaxed">
          Pagamenti gestiti da Stripe. Nessuna registrazione richiesta — il tuo sblocco è legato in modo anonimo a questo dispositivo.
        </p>
      </div>
    </div>
  );
}
